import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Container, ColorBar, PageHeader, Picture, QuoteForm, ContactButtons } from "@/components/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GiftingItemDetailPage() {
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [category, setCategory] = useState(null);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    Promise.all([
      axios.get(`${API}/gifting-items/${itemId}`),
      axios.get(`${API}/gifting-categories`),
      axios.get(`${API}/colors`),
    ]).then(([it, cats, cols]) => {
      setItem(it.data);
      setCategory(cats.data.find((c) => c.id === it.data.category_id) || null);
      setColors(cols.data);
    }).catch(() => setItem(null)).finally(() => setLoading(false));
  }, [itemId]);

  if (loading) {
    return <div className="min-h-screen bg-paper"><Navbar /><div className="pt-40 pb-32 text-center text-graphite" role="status">Loading…</div><Footer /></div>;
  }
  if (!item) {
    return (
      <div className="min-h-screen bg-paper">
        <Navbar />
        <PageHeader title="We couldn't find that item" lede="It may no longer be in the catalogue.">
          <Link to="/corporate-gifting" className="btn btn-ink">Back to corporate gifting</Link>
        </PageHeader>
        <Footer />
      </div>
    );
  }

  const images = item.images?.length ? item.images : [];
  const itemColors = (item.available_colors || []).map((id) => colors.find((c) => c.id === id)).filter(Boolean);

  return (
    <div data-testid="gifting-item-detail-page" className="min-h-screen bg-paper">
      <Navbar />
      <header className="pt-28 md:pt-32 border-b border-ink">
        <Container className="pb-12 md:pb-16">
          <nav aria-label="Breadcrumb" className="mb-8 text-sm text-graphite flex flex-wrap gap-x-2 gap-y-1">
            <Link to="/" className="hover:text-ink hover:underline underline-offset-4">Home</Link><span aria-hidden="true">/</span>
            <Link to="/corporate-gifting" className="hover:text-ink hover:underline underline-offset-4">Corporate gifting</Link>
            {category && <><span aria-hidden="true">/</span><Link to={`/corporate-gifting/${category.id}`} className="hover:text-ink hover:underline underline-offset-4">{category.name}</Link></>}
            <span aria-hidden="true">/</span><span className="text-ink">{item.name}</span>
          </nav>

          <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-14">
            <div>
              <div className="relative border border-ink bg-[#ECEAE3]">
                <Picture src={images[activeImg]} alt={item.name} className="w-full aspect-square" />
                {images.length > 1 && (
                  <>
                    <button type="button" aria-label="Previous image" onClick={() => setActiveImg((p) => (p - 1 + images.length) % images.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-paper border border-ink flex items-center justify-center hover:bg-ink hover:text-paper transition-colors">
                      <ChevronLeft size={20} />
                    </button>
                    <button type="button" aria-label="Next image" onClick={() => setActiveImg((p) => (p + 1) % images.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-paper border border-ink flex items-center justify-center hover:bg-ink hover:text-paper transition-colors">
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}
              </div>
              {images.length > 1 && (
                <div className="mt-3 flex gap-3 overflow-x-auto">
                  {images.map((img, i) => (
                    <button key={i} type="button" onClick={() => setActiveImg(i)} aria-label={`Image ${i + 1}`}
                      className={`w-20 h-20 shrink-0 border-2 overflow-hidden ${i === activeImg ? "border-process-magenta" : "border-ink/20 hover:border-ink"}`}>
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:pt-2">
              <h1 data-testid="item-detail-name" className="font-display font-bold text-4xl sm:text-6xl tracking-[-0.035em] leading-[1] text-ink">{item.name}</h1>
              <p className="mt-6 font-display text-ink">
                <span className="text-graphite">From </span>
                <span className="text-4xl font-bold">₹{item.starting_price}</span>
                <span className="text-graphite"> a piece</span>
              </p>
              {item.description && <p className="mt-6 text-lg text-graphite leading-relaxed whitespace-pre-wrap max-w-[60ch]">{item.description}</p>}

              {itemColors.length > 0 && (
                <div className="mt-8">
                  <h2 className="font-display font-semibold text-ink">Colours</h2>
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {itemColors.map((c) => (
                      <li key={c.id} className="flex items-center gap-2 border border-ink rounded-full pl-1.5 pr-3 py-1">
                        <span className="w-5 h-5 rounded-full border border-ink/30" style={{ backgroundColor: c.color_code }} aria-hidden="true" />
                        <span className="text-sm text-ink">{c.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-10 pt-8 border-t border-ink flex flex-wrap gap-3">
                <a href="#item-enquiry" className="btn btn-magenta">Enquire about this item</a>
                <ContactButtons message={`Hi, I'm interested in ${item.name}.`} />
              </div>
            </div>
          </div>
        </Container>
        <ColorBar />
      </header>

      <main>
        <section id="item-enquiry" className="bg-stock py-16 md:py-24 scroll-mt-16">
          <Container className="grid gap-10 lg:grid-cols-[1fr_minmax(0,32rem)] lg:gap-16 items-start">
            <div>
              <h2 className="font-display font-bold text-3xl sm:text-5xl text-ink">Get a price for {item.name}</h2>
              <p className="mt-4 text-lg text-graphite leading-relaxed max-w-md">Tell us how many, which colours, and what branding you need.</p>
            </div>
            <QuoteForm
              testid="item-enquiry-form"
              title=""
              service="Corporate Gifting"
              lockService
              messagePrefix={`Enquiry for: ${item.name}`}
              messageLabel="Quantity, colours and branding"
              messagePlaceholder="e.g. 100 pieces, navy, logo printed on one side"
              submitLabel="Send enquiry"
            />
          </Container>
        </section>
      </main>
      <Footer />
    </div>
  );
}
